import { useState, useEffect } from "react";
import { UseFormReturn, useController } from "react-hook-form";

import { Field } from "../../types";

import ContentArea from "@/components/ContentArea";

interface ContentAreaFieldProps {
  size?: "desktop" | "dialog";
  field: Field;
  form: UseFormReturn;
  stepId: string;
}

export const ContentAreaField = ({
  size = "desktop",
  field,
  form,
  stepId,
}: ContentAreaFieldProps) => {
  const { trigger, watch } = form;
  const [isInitiated, setIsInitiated] = useState(false);
  const fieldsInputs = watch(stepId);
  const isDisplayed = field.showIf ? field.showIf?.(fieldsInputs) : true;
  const key = `${stepId}.${field.id}`;

  const {
    field: { onChange, value, onBlur },
    fieldState: { error, isDirty },
  } = useController({
    rules: {
      required: isDisplayed ? field.required : false,
      validate: val => (isDisplayed ? field.validation?.(val, fieldsInputs) : undefined),
    },
    control: form.control,
    name: key,
  });

  useEffect(() => {
    if (isDisplayed && !isInitiated) {
      setIsInitiated(true);
      trigger(stepId);
    }
  }, [isDisplayed, stepId, trigger, isInitiated]);

  if (!isDisplayed) {
    return null;
  }

  return (
    <div className="relative">
      <div className="mb-2">
        {field.label && (
          <label className="mb-1 block text-sm font-medium text-gray-700">
            {typeof field.label === "function" ? field.label(fieldsInputs) : field.label}
          </label>
        )}
        {field.tooltip && <p className="mb-2 text-xs text-gray-500">{field.tooltip}</p>}
      </div>
      <ContentArea
        className={`h-full max-h-[460px] resize-y overflow-auto ${
          size === "desktop" ? "min-h-[120px]" : "min-h-[80px]"
        }`}
        hideContextMenu
        placeholder={
          typeof field.placeholder === "function"
            ? field.placeholder(fieldsInputs)
            : field.placeholder || "Enter content..."
        }
        value={value || ""}
        onChange={onChange}
        onBlur={onBlur}
        readOnly={field.readOnly}
      />
      {error && isDirty && <p className="mt-1 text-sm text-red-500">{error.message}</p>}
    </div>
  );
};
